import React, { useState, useEffect, useCallback } from 'react';
import { vendutoApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, ArrowRightLeft, ArrowUpRight, ArrowDownRight, Minus, Sparkles } from 'lucide-react';
import VendutoHeader from '../components/venduto/VendutoHeader';
import PeriodPicker from '../components/venduto/PeriodPicker';
import { useVendutoPeriodo } from '../hooks/useVendutoPeriodo';
import {
  periodoRange, selezionePrecedente, fmtMetrica, fmtDelta, deltaSegno, fmtNum,
} from '../lib/venduto';
import { cn } from '../lib/utils';

const VendutoConfrontoPage = () => {
  const [stato, setStato] = useState(null);
  const [selB] = useVendutoPeriodo(stato);          // periodo recente (default: selezione condivisa)
  const [periodoB, setPeriodoB] = useState(null);
  const [periodoA, setPeriodoA] = useState(null);   // periodo più vecchio
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    vendutoApi.getStato().then(setStato).catch(() => {});
  }, []);

  useEffect(() => {
    if (selB && !periodoB) {
      setPeriodoB(selB);
      setPeriodoA(selezionePrecedente(selB));
    }
  }, [selB, periodoB]);

  // Le due selezioni condividono la granularità (confronto omogeneo)
  const cambiaB = (v) => {
    setPeriodoB(v);
    if (v.granularita !== periodoA?.granularita) setPeriodoA({ ...selezionePrecedente(v) });
    else setPeriodoA({ ...periodoA, granularita: v.granularita });
  };
  const cambiaA = (v) => setPeriodoA({ ...v, granularita: periodoB.granularita });

  const confronta = useCallback(async () => {
    if (!periodoA || !periodoB || !stato) return;
    setLoading(true);
    try {
      const a = periodoRange(periodoA, stato);
      const b = periodoRange(periodoB, stato);
      setDati(await vendutoApi.getConfronto(a, b));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel confronto');
    } finally {
      setLoading(false);
    }
  }, [periodoA, periodoB, stato]);

  useEffect(() => { confronta(); }, [confronta]);

  const labelA = periodoA && stato ? periodoRange(periodoA, stato).label : '';
  const labelB = periodoB && stato ? periodoRange(periodoB, stato).label : '';

  return (
    <div className="min-h-screen pb-24">
      <VendutoHeader titolo="Confronto periodi" sottotitolo={labelA && labelB ? `${labelA} vs ${labelB}` : null} />

      <main className="px-4 py-5 max-w-lg mx-auto space-y-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ArrowRightLeft className="w-4 h-4 text-primary" /> Periodi a confronto
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {periodoB && (
              <>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Periodo recente (destra)
                  </p>
                  <PeriodPicker value={periodoB} onChange={cambiaB} archivio={stato} testid="periodo-b" />
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                    Periodo di confronto (sinistra)
                  </p>
                  <PeriodPicker value={periodoA} onChange={cambiaA} archivio={stato} testid="periodo-a" />
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {loading && <div className="flex justify-center py-10"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>}

        {!loading && dati && (
          <>
            {dati.blocchi.map((b) => (
              <Card key={b.servizio} className={cn(b.servizio === 'totale' && 'border-primary/40')}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{b.label}</CardTitle>
                </CardHeader>
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-xs" data-testid={`confronto-${b.servizio}`}>
                    <thead className="bg-secondary">
                      <tr>
                        <th className="p-2 text-left font-semibold">Metrica</th>
                        <th className="p-2 text-right font-semibold whitespace-nowrap">{labelA}</th>
                        <th className="p-2 text-right font-semibold whitespace-nowrap">{labelB}</th>
                        <th className="p-2 text-right font-semibold">Δ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {b.righe.map((r) => {
                        const segno = deltaSegno(r);
                        const Icona = segno > 0 ? ArrowUpRight : segno < 0 ? ArrowDownRight : Minus;
                        return (
                          <tr key={r.key} className="border-t border-border/60">
                            <td className="p-2 text-muted-foreground">{r.label}</td>
                            <td className="p-2 text-right font-mono-numbers">{fmtMetrica(r.valore_a, r.tipo)}</td>
                            <td className="p-2 text-right font-mono-numbers font-semibold">{fmtMetrica(r.valore_b, r.tipo)}</td>
                            <td
                              className={cn(
                                'p-2 text-right font-mono-numbers whitespace-nowrap',
                                segno > 0 && 'text-green-700',
                                segno < 0 && 'text-red-700',
                                segno === 0 && 'text-muted-foreground'
                              )}
                              data-testid={`delta-${b.servizio}-${r.key}`}
                            >
                              <span className="inline-flex items-center gap-1">
                                <Icona className="w-3 h-3" /> {fmtDelta(r)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            ))}

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" /> Best seller per categoria
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4" data-testid="bestseller-card">
                {dati.bestseller.length === 0 && (
                  <p className="text-sm text-muted-foreground italic">Nessun prodotto nei periodi scelti.</p>
                )}
                {dati.bestseller.map((c) => (
                  <div key={c.categoria} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold">{c.label}</p>
                      <div className="flex gap-1">
                        {c.nuovi > 0 && (
                          <Badge className="bg-green-100 text-green-800 border-green-300 text-[10px]">
                            {c.nuovi} nuovi
                          </Badge>
                        )}
                        {c.scomparsi > 0 && (
                          <Badge className="bg-red-100 text-red-800 border-red-300 text-[10px]">
                            {c.scomparsi} scomparsi
                          </Badge>
                        )}
                      </div>
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-muted-foreground">
                          <th className="text-left font-normal py-1">Prodotto</th>
                          <th className="text-right font-normal py-1">{labelA}</th>
                          <th className="text-right font-normal py-1">{labelB}</th>
                          <th className="text-right font-normal py-1">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.prodotti.map((p) => (
                          <tr key={p.prodotto} className="border-t border-border/60">
                            <td className="py-1.5 pr-2 truncate max-w-[130px]">
                              {p.prodotto}
                              {p.stato === 'nuovo' && <span className="ml-1 text-green-700">•nuovo</span>}
                              {p.stato === 'scomparso' && <span className="ml-1 text-red-700">•uscito</span>}
                            </td>
                            <td className="py-1.5 text-right font-mono-numbers">{fmtNum(p.quantita_a, 0)}</td>
                            <td className="py-1.5 text-right font-mono-numbers font-semibold">{fmtNum(p.quantita_b, 0)}</td>
                            <td className={cn(
                              'py-1.5 text-right font-mono-numbers',
                              p.delta > 0 ? 'text-green-700' : p.delta < 0 ? 'text-red-700' : 'text-muted-foreground'
                            )}>
                              {p.delta > 0 ? '+' : ''}{fmtNum(p.delta, 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default VendutoConfrontoPage;
